import { connect } from 'react-redux';
import { get, keys } from 'lodash';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';

import { openSmallModal } from '@actions/modal';
import { showErrorMessage } from '@actions/messageconfirmation';

import {
  Button,
  Dropdown,
  Franco,
  NestedList,
  Tooltip,
} from '@commons/utils/styledLibraryComponents';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { getTheme } from '@commons/utils/theme';
import { numberToFixed } from '@commons/utils/format';

import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { DeepsightComponentLoader } from '@commons/DeepsightComponents';
import { getUserTimezone } from '@commons/utils/date';
import { LeftRightSplitter } from '@commons/LeftRightSplitter';
import { PeriodDatePicker } from '@commons/DatePickers/PeriodDatePicker';
import DisplayNumber from '@commons/DisplayNumber';
import NavigationBreadCrumb from '@commons/Breadcrumb/NavigationBreadCrumb';
import StoresWithActiveProperty from '@commons/DropdownItems/StoresWithActiveProperty';

import { fetchGroupsByStoreIds } from '@orders/OrderAnalyticsModulesContainer/utils/fetches';
import { usePeriodDatePickerState } from '@hooks/usePeriodDatePickerState';

import { getClientInfo } from '@selectors/client';
import { getSalesPointStores } from '@selectors/stores';

import { forecast as forecastService } from '@services/forecast';

import getStatsFromData from '../../utils/getStatsFromData';

import {
  ButtonContainer,
  Container,
  ContentContainer,
  FlexedGapContainer,
  ListContainer,
  SidePaddedGapContainer,
  InfoBoxContainer,
  InfoBoxLabel,
} from './styledComponents';
import { exportPastMix } from './exports/export';
import { getListHeaders } from './config';
import ExportPastMixByStoreModal from './exports/ExportPastMixByStoreModal';
import PastMixSimpleModifierManagement from './PastMixSimpleModifierManagement';

const PRODUCT_LINK_PREFIX = '/admin/products/';
const PRODUCT_LINK_SUFFIX = '/details';
const PERIOD_NB_DAYS_DEFAULT = 8;
const USER_TIMEZONE = getUserTimezone();
const YESTERDAY = moment.tz(USER_TIMEZONE).subtract(1, 'days').endOf('day');
const DEFAULT_CATEGORY = 'none value';

const PastMix = (props) => {
  const {
    match,
    stores,
    currency,
    openSmallModal,
    showErrorMessage,
    client: { useTtc, storeName, hasSimpleModifierManagement },
  } = props;

  if (hasSimpleModifierManagement) {
    return <PastMixSimpleModifierManagement match={match} />;
  }

  const theme = getTheme();
  const firstActiveStore = stores.find(({ active }) => active);
  const defaultSelectedStores = firstActiveStore ? [firstActiveStore] : [];

  const [isLoading, setIsLoading] = useState(false);

  const [data, setData] = useState([]);

  const [displayedStores, setDisplayedStores] = useState(stores);
  const [selectedStores, setSelectedStores] = useState(defaultSelectedStores);
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);

  const [headers] = useState(getListHeaders());

  const defaultStartDate = moment
    .tz(USER_TIMEZONE)
    .subtract(PERIOD_NB_DAYS_DEFAULT, 'days')
    .startOf('day');
  const [maxFutureDate, setMaxFutureDate] = useState(YESTERDAY);

  const periodPickerState = usePeriodDatePickerState(
    defaultStartDate,
    maxFutureDate,
    USER_TIMEZONE,
  );
  const [headerData, setHeaderData] = useState({});

  const triggerExportAnalyticsByStore = () => {
    openSmallModal({
      component: ExportPastMixByStoreModal,
      currency,
      context: {
        stores: selectedStores,
        startDate: periodPickerState.startDate,
        endDate: periodPickerState.endDate,
      },
    });
  };

  const actions = [
    {
      label: i18next.t('FORECAST.PAST_MIX.EXPORT_ACTION'),
      icon: `/images/inpulse/file-download-black-small.svg`,
      handleClick: () => {
        exportPastMix({
          data,
          useTtc,
          currency,
          clientStoreName: storeName,
          startDate: periodPickerState.startDate,
          endDate: periodPickerState.endDate,
          stores: selectedStores,
        });
      },
    },
    {
      label: i18next.t('FORECAST.PAST_MIX.EXPORT_BY_STORE_ACTION'),
      icon: `/images/inpulse/file-download-black-small.svg`,
      handleClick: triggerExportAnalyticsByStore,
    },
  ];

  const formatForNestedList = (pastMixData) => {
    const productsByCategory = get(pastMixData, 'productsByCategory', []);

    return keys(productsByCategory).reduce((result, category) => {
      const categoryName =
        category === DEFAULT_CATEGORY ? i18next.t('GENERAL.NONE_VALUE') : category;

      const data = productsByCategory[category];

      const nestedData = data.nestedData.map((product) => ({
        id: product.id,
        name: product.name,
        category: categoryName,
        link: `${PRODUCT_LINK_PREFIX}${product.id}${PRODUCT_LINK_SUFFIX}`,
        ...getStatsFromData(product),
      }));

      const item = {
        name: categoryName,
        nestedData: nestedData,
        ...getStatsFromData(data),
      };

      result.push(item);
      return result;
    }, []);
  };

  const getPastMixData = async () => {
    setIsLoading(true);

    try {
      const startDate = moment
        .tz(periodPickerState.startDate, USER_TIMEZONE)
        .format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY);
      const endDate = moment
        .tz(periodPickerState.endDate, USER_TIMEZONE)
        .format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY);

      const selectedStoreIds = selectedStores.map(({ id }) => id);

      const pastMix = await forecastService.getPastProductMix(selectedStoreIds, startDate, endDate);

      const formattedData = formatForNestedList(pastMix);

      setData(formattedData);

      const marginRatio = get(pastMix, 'margin.ratioToTurnover', null);

      setHeaderData({
        turnover: get(pastMix, 'turnover', null),
        turnoverExcludingTaxes: get(pastMix, 'turnoverExcludingTaxes', null),
        margin: get(pastMix, 'margin.total', null),
        ratio: numberToFixed(marginRatio, 2, null, ' %'),
      });
    } catch {
      setData([]);
      showErrorMessage(i18next.t('FORECAST.PAST_MIX.FETCH_DATA_FAILURE'));
    } finally {
      setIsLoading(false);
    }
  };

  const getCalendarInfo = () => ({
    globalInfo: i18next.t('FORECAST.PAST_MIX.DATEPICKER_TOOLTIP', {
      storeNameSingle: getClientStoreNameTranslation(storeName, false).toLowerCase(),
      storeNamePlural: getClientStoreNameTranslation(storeName, true).toLowerCase(),
    }),
  });

  const getFirstActiveStore = (storesList) => {
    if (!storesList.length) {
      return [];
    }

    const firstActiveStore = storesList.find(({ active }) => active);

    return firstActiveStore ? [firstActiveStore] : [];
  };

  useEffect(() => {
    if (!stores || !stores.length) {
      return;
    }

    (async () => {
      const fetchedGroups = await fetchGroupsByStoreIds(
        stores.map(({ id }) => id),
        showErrorMessage,
      );

      setGroups(fetchedGroups);
    })();
  }, []);

  useEffect(() => {
    const periodDiff = periodPickerState.endDate.diff(periodPickerState.startDate, 'days');

    if (selectedStores.length > 1 && periodDiff > 31) {
      periodPickerState.handleSelectedDates(
        {
          startDate: defaultStartDate,
          endDate: YESTERDAY,
        },
        true,
      );
    }
  }, [selectedStores]);

  useEffect(() => {
    const maxEndDate = moment
      .tz(periodPickerState.startDate, USER_TIMEZONE)
      .add(1, selectedStores.length > 1 ? 'month' : 'year');

    setMaxFutureDate(moment.min(YESTERDAY, maxEndDate));
  }, [periodPickerState.startDate]);

  useEffect(() => {
    if (periodPickerState.startDate && periodPickerState.endDate) {
      const periodDiff = periodPickerState.endDate.diff(periodPickerState.startDate, 'days');

      if (!selectedStores.length || (selectedStores.length > 1 && periodDiff > 31)) {
        return;
      }

      (async function loadData() {
        await getPastMixData();
      })();
    }
  }, [selectedStores, periodPickerState.startDate, periodPickerState.endDate]);

  return (
    <Container>
      <NavigationBreadCrumb featurePath={match.path} />
      <ContentContainer>
        <SidePaddedGapContainer>
          <LeftRightSplitter
            left={
              <FlexedGapContainer>
                {!!groups.length && (
                  <Dropdown
                    iconSrc="/images/inpulse/group-dmgrey-small.svg"
                    isDisabled={isLoading}
                    isUniqueSelection={false}
                    items={groups}
                    searchPlaceholder={i18next.t('GENERAL.SEARCH')}
                    selectedItems={selectedGroups}
                    onSelectionChange={(items) => {
                      setSelectedGroups(items);

                      if (!items.length) {
                        // Reset selected stores and displayed stores with stores from redux
                        setDisplayedStores(stores);
                        setSelectedStores(getFirstActiveStore(stores));
                        return;
                      }

                      const groupsStoreIds = items.flatMap(({ storeIds }) => storeIds);
                      const filteredStores = stores.filter(({ id }) => groupsStoreIds.includes(id));

                      setDisplayedStores(filteredStores); // Can be empty
                      setSelectedStores(getFirstActiveStore(filteredStores));
                    }}
                  />
                )}

                <Dropdown
                  iconSrc="/images/inpulse/pin-black-small.svg"
                  isDisabled={isLoading}
                  isUniqueSelection={false}
                  items={displayedStores.map((store) => ({
                    ...store,
                    renderValue: () => StoresWithActiveProperty(store),
                  }))}
                  searchPlaceholder={i18next.t('GENERAL.SEARCH')}
                  selectedItems={selectedStores}
                  isRequired
                  onSelectionChange={setSelectedStores}
                />
                <PeriodDatePicker
                  calendarInfo={getCalendarInfo()}
                  disabled={isLoading}
                  endDate={periodPickerState.endDate}
                  focusedDateInput={periodPickerState.focusedDateInput}
                  maxFutureDate={maxFutureDate}
                  setFocusedDateInput={periodPickerState.setFocusedDateInput}
                  startDate={periodPickerState.startDate}
                  timezone="Europe/Paris" // TODO - [TIMEZONES] replace this when IPP-5225 TIMEZONES Forecast Module is finished
                  onDatesChange={periodPickerState.handleSelectedDates}
                />

                <InfoBoxContainer>
                  <InfoBoxLabel>
                    {`${i18next.t(
                      useTtc ? 'GENERAL.TURNOVER_TAX_INCLUDED' : 'GENERAL.TURNOVER_EX_TAX',
                    )} :`}
                    <Tooltip
                      text={`${i18next.t(
                        useTtc
                          ? 'FORECAST.PRODUCT_MIX.TURNOVER_BOX_TOOLTIP_TEXT'
                          : 'FORECAST.PRODUCT_MIX.TURNOVER_EXCLUDING_TAXES_BOX_TOOLTIP_TEXT',
                      )}`}
                    />
                  </InfoBoxLabel>

                  <div className="kpi">
                    {isLoading ? (
                      <DeepsightComponentLoader height={16} width={16} />
                    ) : (
                      <DisplayNumber
                        currency={currency}
                        number={get(
                          headerData,
                          useTtc ? 'turnover' : 'turnoverExcludingTaxes',
                          null,
                        )}
                        displayCurrencyCode
                      />
                    )}
                  </div>
                </InfoBoxContainer>

                <Franco
                  background={theme.colors.greys.dark}
                  color={theme.colors.greys.lightest}
                  content={
                    <DisplayNumber
                      number={get(headerData, 'margin', null)}
                      displayCurrencyCode
                      shortenDigits
                    />
                  }
                  infoTooltip={i18next.t('GENERAL.METRIC_MARGIN_EX_TAX_TOOLTIP')}
                  labelContent={get(headerData, 'ratio', '')}
                  title={`${i18next.t('GENERAL.METRIC_MARGIN_EX_TAX_WITHOUT_CURRENCY')} :`}
                  width={'248px'}
                  hasBorder
                />
              </FlexedGapContainer>
            }
            right={
              <ButtonContainer style={{ display: 'flex', flex: 1, justifyContent: 'end' }}>
                <Button
                  actions={actions}
                  color={'inpulse-outline'}
                  iconDropdown={'/images/inpulse/carret-black.svg'}
                  iconOnLeft={false}
                  label={i18next.t('GENERAL.ACTIONS')}
                />
              </ButtonContainer>
            }
          />
        </SidePaddedGapContainer>
        <ListContainer>
          <NestedList
            data={data}
            defaultOrderBy={useTtc ? 'mix' : 'mixExcludingTaxes'}
            defaultOrderType={'desc'}
            headers={headers}
            isLoading={isLoading}
            labelEmptyState={i18next.t('GENERAL.NO_RESULT')}
            minWidth={'978px'}
            hasNestedData
          />
        </ListContainer>
      </ContentContainer>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  userRights: state.baseReducer.userRights,
  stores: getSalesPointStores(state.baseReducer.stores),
  client: getClientInfo(state.baseReducer.user),
  currency: state.baseReducer.currency,
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  openSmallModal: (params) => {
    dispatch(openSmallModal(params));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(PastMix);
